import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Small inline CustomButton for convenience
const CustomButton = ({ children, onClick, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 py-1 rounded text-xs ${className}`}
  >
    {children}
  </button>
);

export default function LocationDeviceHistoryTable() {
  const navigate = useNavigate();

  // sample rows similar to the screenshot
  const sample = {
    deviceId: "0944f940847c5363",
    mobile: "realme RMX3612 Android 14",
    mobileTime: "01 Nov 09:22 PM",
    serverTime: "01 Nov 09:22 PM",
    ip: "110.227.53.106",
    latitude: "22.771995",
    longitude: "74.586369",
    distanceKm: "128.84",
    kmCovered: "265.89",
    mapUrl: "#",
    address: "86, Laxmi Nagar, Jhabua, Madhya Pradesh 457661, India",
    addressTime: "09:22 PM",
    accuracy: 68,
    dataFlag: "M",
    mock: false,
    type: "Google",
    provider: "fused",
    speed: 0,
    removeId: "529376957",
  };

  const [rows] = useState(Array.from({ length: 7 }).map((_, i) => ({ ...sample, sno: 33 + i })));
  const [selected, setSelected] = useState(() => ({}));

  const totalKmCovered = useMemo(() => rows.reduce((s, r) => s + Number(r.kmCovered || 0), 0), [rows]);
  const totalDistance = useMemo(() => rows.reduce((s, r) => s + Number(r.distanceKm || 0), 0), [rows]);

  const toggleSelect = (removeId) => {
    setSelected((prev) => ({ ...prev, [removeId]: !prev[removeId] }));
  };

  const handleRemove = (removeId) => {
    alert(`Remove clicked for ${removeId}`);
  };

  const openMap = (row) => {
    // open a map or location-detail page in new tab
    // replace with real url if needed
    const url = `/reporting/location-detail?lat=${encodeURIComponent(row.latitude)}&lng=${encodeURIComponent(row.longitude)}&time=${encodeURIComponent(row.mobileTime)}`;
    window.open(url, "_blank");
  };

  // column widths (pixels) to make compact layout
  const colWidths = [48, 220, 220, 110, 110, 120, 100, 100, 90, 90, 60, 260, 120, 80, 60, 80, 80, 110, 120];
  // mapping of column keys to show order
  return (
    <div className="w-full p-3 bg-white">
      <div className="max-w-full overflow-x-auto">
        <h2 className="text-sm font-bold mb-2">Device Location Time</h2>

        <table className="table-fixed border-collapse text-xs" style={{ width: "max-content" }}>
          <colgroup>
            {colWidths.map((w, i) => (
              <col key={i} style={{ width: `${w}px` }} />
            ))}
          </colgroup>

          <thead>
            <tr className="bg-cyan-100">
              <th className="border px-2 py-1 text-center">S.No</th>
              <th className="border px-2 py-1 text-center">Device ID</th>
              <th className="border px-2 py-1 text-center">Mobile</th>
              <th className="border px-2 py-1 text-center">Mobile Time</th>
              <th className="border px-2 py-1 text-center">Server Time</th>
              <th className="border px-2 py-1 text-center">I.P.</th>
              <th className="border px-2 py-1 text-center">Latitude</th>
              <th className="border px-2 py-1 text-center">Longitude</th>
              <th className="border px-2 py-1 text-center">Distance (K.M.)</th>
              <th className="border px-2 py-1 text-center">K.M. Covered</th>
              <th className="border px-2 py-1 text-center">Map</th>
              <th className="border px-2 py-1 text-center">Address</th>
              <th className="border px-2 py-1 text-center">Accuracy (68% In Meter)</th>
              <th className="border px-2 py-1 text-center">Data</th>
              <th className="border px-2 py-1 text-center">Mock</th>
              <th className="border px-2 py-1 text-center">Type</th>
              <th className="border px-2 py-1 text-center">Provider</th>
              <th className="border px-2 py-1 text-center">Speed</th>
              <th className="border px-2 py-1 text-center">Remove Location</th>
              <th className="border px-2 py-1 text-center">Remove Selected</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr key={row.sno} className="align-top even:bg-white odd:bg-gray-50">
                <td className="border px-2 py-1 text-center">{row.sno}</td>
                <td className="border px-2 py-1 break-words">{row.deviceId}</td>
                <td className="border px-2 py-1 break-words">{row.mobile}</td>
                <td className="border px-2 py-1 text-center">{row.mobileTime}</td>
                <td className="border px-2 py-1 text-center">{row.serverTime}</td>
                <td className="border px-2 py-1 text-center">{row.ip}</td>
                <td className="border px-2 py-1 text-center">{row.latitude}</td>
                <td className="border px-2 py-1 text-center">{row.longitude}</td>
                <td className="border px-2 py-1 text-center font-semibold bg-pink-50">{row.distanceKm}</td>
                <td className="border px-2 py-1 text-center font-semibold">{row.kmCovered}</td>

                <td className="border px-2 py-1 text-center">
                  <a href="#" onClick={(e) => { e.preventDefault(); openMap(row); }} className="text-blue-600 underline">view</a>
                </td>

                <td className="border px-2 py-1">
                  {/* address with anchor that opens google maps in new tab */}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(row.latitude + "," + row.longitude)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 underline block whitespace-normal break-words"
                    title={`${row.address} ${row.addressTime}`}
                  >
                    {row.address}
                    <div className="text-[10px] text-gray-500 mt-1">{row.addressTime}</div>
                  </a>
                </td>

                <td className="border px-2 py-1 text-center">{row.accuracy}</td>
                <td className="border px-2 py-1 text-center">{row.dataFlag}</td>
                <td className="border px-2 py-1 text-center text-green-600">{String(row.mock)}</td>
                <td className="border px-2 py-1 text-center">{row.type}</td>
                <td className="border px-2 py-1 text-center">{row.provider}</td>
                <td className="border px-2 py-1 text-center">{row.speed}</td>

                <td className="border px-2 py-1 text-center">
                  <CustomButton onClick={() => handleRemove(row.removeId)} className="bg-gray-100">Remove</CustomButton>
                </td>

                <td className="border px-2 py-1 text-center">
                  <input type="checkbox" checked={!!selected[row.removeId]} onChange={() => toggleSelect(row.removeId)} />
                  <div className="text-[10px] text-gray-500 mt-1">{row.removeId}</div>
                </td>
              </tr>
            ))}

            {/* footer row with totals */}
            <tr>
              <td colSpan={8} className="border px-2 py-1 text-right font-semibold">Total</td>
              <td className="border px-2 py-1 text-center font-semibold">{totalDistance.toFixed(2)}</td>
              <td className="border px-2 py-1 text-center font-semibold">{totalKmCovered.toFixed(2)}</td>
              <td colSpan={10} className="border px-2 py-1" />
            </tr>
          </tbody>
        </table>

        <div className="mt-4 flex gap-2 items-center">
          <CustomButton onClick={() => window.print()} className="bg-blue-600 text-white">PRINT</CustomButton>
          <CustomButton onClick={() => alert('Export to excel placeholder')} className="bg-green-600 text-white">Export To Excel</CustomButton>
          <CustomButton onClick={() => alert('Close placeholder')} className="bg-gray-300">CLOSE</CustomButton>
        </div>
      </div>
    </div>
  );
}
